/**
 * MeetingAgendaTab Component
 *
 * Meeting agenda with attendance tracking, task tracker, and checklist.
 * Data is persisted to Gobodo via Meeting_Agenda__c object.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Calendar,
  Users,
  Plus,
  Trash2,
  GripVertical,
  User,
  ClipboardList,
  CheckSquare,
  MessageSquarePlus,
  Loader2,
  Save,
  Cloud,
  CloudOff,
} from 'lucide-react';
import { useUsers, User as UserType } from '../../services/queries/userQueries';
import {
  useMeetingAgenda,
  useCreateMeetingAgenda,
  useUpdateMeetingAgenda,
  MeetingAttendanceRecord,
  MeetingAgendaItem,
  MeetingTaskItem,
} from '../../services/queries/cyberSweepQueries';
import { filterAttendanceEligibleUsers } from '../../utils/cyberSweepUserFilters';

interface MeetingAgendaTabProps {
  selectedWeek: string; // Format: YYYY-Www
  weekStartDate: string; // Monday date in yyyy-MM-dd format
}

// Re-export types for local use (imported from cyberSweepQueries)
type AttendanceRecord = MeetingAttendanceRecord;
type AgendaItem = MeetingAgendaItem;
type TaskItem = MeetingTaskItem;

// Default agenda items
const DEFAULT_AGENDA_ITEMS: Omit<AgendaItem, 'id'>[] = [
  { text: 'Google Alert Monitoring', notes: '', completed: false, isDefault: true, sortOrder: 0, showNotes: false },
  { text: 'Websites issues and concerns', notes: '', completed: false, isDefault: true, sortOrder: 1, showNotes: false },
  { text: "Client's concerns", notes: '', completed: false, isDefault: true, sortOrder: 2, showNotes: false },
  { text: 'Updates of client requested Removal', notes: '', completed: false, isDefault: true, sortOrder: 3, showNotes: false },
  { text: 'Unfinished files from last week', notes: '', completed: false, isDefault: true, sortOrder: 4, showNotes: false },
];

// Task status options
const TASK_STATUS_OPTIONS: { value: TaskItem['status']; label: string }[] = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
];

const getTaskStatusStyles = (status: TaskItem['status']): string => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
    case 'in_progress':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
};

// Generate unique ID
const generateId = (): string => {
  return `agenda-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Format date for display (MM/DD/YYYY)
const formatDateForDisplay = (isoDate: string): string => {
  if (!isoDate) return '';
  try {
    const datePart = isoDate.includes('T') ? isoDate.split('T')[0] : isoDate;
    const [year, month, day] = datePart.split('-');
    if (year && month && day) {
      return `${month}/${day}/${year}`;
    }
    return isoDate;
  } catch {
    return isoDate;
  }
};

// Initialize default agenda items with IDs
const getDefaultAgendaItems = (): AgendaItem[] => {
  return DEFAULT_AGENDA_ITEMS.map((item, index) => ({
    ...item,
    id: `default-${index}`,
  }));
};

const MeetingAgendaTab: React.FC<MeetingAgendaTabProps> = ({ selectedWeek, weekStartDate }) => {
  const { data: allUsers = [] } = useUsers({});
  // Filter to active CyberSweep users for attendance tracking
  const cyberSweepUsers = useMemo(
    () => filterAttendanceEligibleUsers(allUsers),
    [allUsers]
  );

  // Gobodo hooks
  const { data: agendaRecord, isLoading: isLoadingAgenda } = useMeetingAgenda(weekStartDate);
  const createAgenda = useCreateMeetingAgenda();
  const updateAgenda = useUpdateMeetingAgenda();

  // Local state for optimistic updates
  const [meetingDate, setMeetingDate] = useState(weekStartDate);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [agendaItems, setAgendaItems] = useState<AgendaItem[]>(() => getDefaultAgendaItems());
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [newItemText, setNewItemText] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [, setHasInitialized] = useState(false);

  // Track if data has been loaded from Gobodo
  const isDataLoaded = useRef(false);

  // Refs to access current state values without causing dependency updates
  const attendanceRef = useRef(attendance);
  const tasksRef = useRef(tasks);
  const agendaItemsRef = useRef(agendaItems);
  const meetingDateRef = useRef(meetingDate);

  // Keep refs in sync with state
  useEffect(() => { attendanceRef.current = attendance; }, [attendance]);
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);
  useEffect(() => { agendaItemsRef.current = agendaItems; }, [agendaItems]);
  useEffect(() => { meetingDateRef.current = meetingDate; }, [meetingDate]);

  // Initialize attendance from team members
  const initializeAttendance = useCallback((): AttendanceRecord[] => {
    return cyberSweepUsers.map(user => ({
      userId: user.id,
      userName: `${user.firstName} ${user.lastName}`,
      status: 'unmarked' as const,
    }));
  }, [cyberSweepUsers]);

  // Reset state when week changes
  useEffect(() => {
    isDataLoaded.current = false;
    setHasInitialized(false);
    setMeetingDate(weekStartDate);
    setAgendaItems(getDefaultAgendaItems());
    setTasks([]);
    setAttendance([]);
    setSaveStatus('idle');
  }, [weekStartDate]);

  // Single unified effect to initialize attendance
  // Handles both: loading from Gobodo AND initializing from users
  useEffect(() => {
    // Skip if still loading or no users available
    if (isLoadingAgenda || cyberSweepUsers.length === 0) {
      return;
    }

    // Skip if already initialized
    if (isDataLoaded.current) {
      return;
    }

    // Load other data from Gobodo if available
    if (agendaRecord) {
      setMeetingDate(agendaRecord.meetingDate || weekStartDate);
      if (agendaRecord.agendaItems.length > 0) {
        setAgendaItems(agendaRecord.agendaItems);
      }
      if (agendaRecord.tasks.length > 0) {
        setTasks(agendaRecord.tasks);
      }
    }

    // Build attendance from current users, preserving any saved statuses from Gobodo
    const gobodoAttendance = agendaRecord?.attendance || [];
    const statusByUserId = new Map<string, 'present' | 'absent' | 'unmarked'>();
    const statusByUserName = new Map<string, 'present' | 'absent' | 'unmarked'>();

    gobodoAttendance.forEach(record => {
      if (record.userId) statusByUserId.set(record.userId, record.status);
      if (record.userName) statusByUserName.set(record.userName.toLowerCase(), record.status);
    });

    const newAttendance: AttendanceRecord[] = cyberSweepUsers.map(user => {
      const userName = `${user.firstName} ${user.lastName}`;
      const statusById = statusByUserId.get(user.id);
      const statusByName = statusByUserName.get(userName.toLowerCase());
      const status = statusById ?? statusByName ?? 'unmarked';

      return { userId: user.id, userName, status };
    });

    setAttendance(newAttendance);
    isDataLoaded.current = true;
    setHasInitialized(true);
  }, [isLoadingAgenda, cyberSweepUsers, agendaRecord, weekStartDate]);

  // Save function - saves to Gobodo only
  // Uses refs for fallback values to avoid dependency cycle with debounced save
  const saveToGobodo = useCallback(async (updates: {
    meetingDate?: string;
    attendance?: AttendanceRecord[];
    tasks?: TaskItem[];
    agendaItems?: AgendaItem[];
  }) => {
    setSaveStatus('saving');
    try {
      if (agendaRecord?.id) {
        // Update existing record
        await updateAgenda.mutateAsync({
          id: agendaRecord.id,
          weekStartDate,
          ...updates,
        });
      } else {
        // Create new record - use refs for fallback values to avoid stale closures
        await createAgenda.mutateAsync({
          weekStartDate,
          meetingDate: updates.meetingDate || meetingDateRef.current,
          attendance: updates.attendance || attendanceRef.current,
          tasks: updates.tasks || tasksRef.current,
          agendaItems: updates.agendaItems || agendaItemsRef.current,
        });
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('[MeetingAgendaTab] Gobodo save failed:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 5000);
    }
  }, [agendaRecord, weekStartDate, updateAgenda, createAgenda]);

  // Manual save function for Save button
  const handleManualSave = useCallback(() => {
    saveToGobodo({
      meetingDate,
      attendance,
      tasks,
      agendaItems,
    });
  }, [saveToGobodo, meetingDate, attendance, tasks, agendaItems]);

  // Update meeting date
  const handleMeetingDateChange = (newDate: string) => {
    setMeetingDate(newDate);
  };

  // Toggle attendance status
  const toggleAttendance = (userId: string, newStatus: 'present' | 'absent') => {
    const updated: AttendanceRecord[] = attendance.map(record =>
      record.userId === userId
        ? { ...record, status: (record.status === newStatus ? 'unmarked' : newStatus) as 'present' | 'absent' | 'unmarked' }
        : record
    );
    setAttendance(updated);
  };

  // Toggle agenda item completion
  const toggleAgendaItem = (itemId: string) => {
    const updated = agendaItems.map(item =>
      item.id === itemId ? { ...item, completed: !item.completed } : item
    );
    setAgendaItems(updated);
  };

  // Add new agenda item
  const addAgendaItem = () => {
    if (!newItemText.trim()) return;

    const newItem: AgendaItem = {
      id: generateId(),
      text: newItemText.trim(),
      notes: '',
      completed: false,
      isDefault: false,
      sortOrder: agendaItems.length,
      showNotes: false,
    };

    setAgendaItems([...agendaItems, newItem]);
    setNewItemText('');
  };

  // Delete agenda item (only custom items)
  const deleteAgendaItem = (itemId: string) => {
    setAgendaItems(agendaItems.filter(item => item.id !== itemId));
  };

  // Update agenda item text
  const updateAgendaItemText = (itemId: string, newText: string) => {
    setAgendaItems(agendaItems.map(item =>
      item.id === itemId ? { ...item, text: newText } : item
    ));
  };

  // Update agenda item notes
  const updateAgendaItemNotes = (itemId: string, newNotes: string) => {
    setAgendaItems(agendaItems.map(item =>
      item.id === itemId ? { ...item, notes: newNotes } : item
    ));
  };

  // Toggle notes visibility (no save needed - UI only)
  const toggleNotesVisibility = (itemId: string, show: boolean) => {
    setAgendaItems(prev =>
      prev.map(item =>
        item.id === itemId ? { ...item, showNotes: show } : item
      )
    );
  };

  // Handle notes blur - hide if empty
  const handleNotesBlur = (itemId: string, notes: string) => {
    if (!notes.trim()) {
      toggleNotesVisibility(itemId, false);
    }
  };

  // Add new task with auto-save
  const addTask = () => {
    const newTask: TaskItem = {
      id: generateId(),
      auditorId: '',
      auditorName: '',
      task: '',
      dueDate: '',
      status: 'not_started',
      isComplete: false,
    };
    const updated = [...tasks, newTask];
    setTasks(updated);
  };

  // Update task
  const updateTask = (taskId: string, updates: Partial<TaskItem>) => {
    const updated = tasks.map(task =>
      task.id === taskId ? { ...task, ...updates } : task
    );
    setTasks(updated);
  };

  // Delete task
  const deleteTask = (taskId: string) => {
    const updated = tasks.filter(task => task.id !== taskId);
    setTasks(updated);
  };

  // Toggle task completion
  const toggleTaskComplete = (taskId: string) => {
    const updated = tasks.map(task =>
      task.id === taskId ? { ...task, isComplete: !task.isComplete } : task
    );
    setTasks(updated);
  };

  // Stats
  const presentCount = attendance.filter(a => a.status === 'present').length;
  const absentCount = attendance.filter(a => a.status === 'absent').length;
  const completedCount = agendaItems.filter(a => a.completed).length;
  const tasksCompletedCount = tasks.filter(t => t.isComplete).length;

  // Loading state
  if (isLoadingAgenda) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
          <span className="ml-2 text-gray-500 dark:text-gray-400">Loading meeting agenda...</span>
        </div>
      </div>
    );
  }

  // Save status indicator
  const SaveStatusIndicator = () => {
    if (saveStatus === 'saving') {
      return (
        <div className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>Saving...</span>
        </div>
      );
    }
    if (saveStatus === 'saved') {
      return (
        <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
          <Cloud className="h-3.5 w-3.5" />
          <span>Saved</span>
        </div>
      );
    }
    if (saveStatus === 'error') {
      return (
        <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
          <CloudOff className="h-3.5 w-3.5" />
          <span>Save failed</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Meeting Date */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <h3 className="font-medium text-gray-900 dark:text-gray-100">Meeting Date</h3>
          </div>
          <div className="flex items-center gap-3">
            <SaveStatusIndicator />
            <button
              onClick={handleManualSave}
              disabled={saveStatus === 'saving'}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 rounded-lg transition-colors"
            >
              <Save className="h-4 w-4" />
              Save
            </button>
            <input
              type="date"
              value={meetingDate}
              onChange={(e) => handleMeetingDateChange(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 ml-7">
          {formatDateForDisplay(meetingDate)}
        </p>
      </div>

      {/* Attendance Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <h3 className="font-medium text-gray-900 dark:text-gray-100">Attendance</h3>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-green-600 dark:text-green-400">{presentCount} present</span>
            <span className="text-red-600 dark:text-red-400">{absentCount} absent</span>
          </div>
        </div>

        {cyberSweepUsers.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
            <Users className="h-8 w-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p>No team members found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {attendance.map((record, index) => (
              <div
                key={record.userId || `attendance-${index}`}
                className="px-4 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <User className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {record.userName}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {/* Present checkbox */}
                  <label className={`flex items-center gap-1.5 px-2 py-1 rounded text-sm cursor-pointer transition-colors ${
                    record.status === 'present'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                      : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}>
                    <input
                      type="checkbox"
                      checked={record.status === 'present'}
                      onChange={() => toggleAttendance(record.userId, 'present')}
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                    />
                    Present
                  </label>

                  {/* Absent checkbox */}
                  <label className={`flex items-center gap-1.5 px-2 py-1 rounded text-sm cursor-pointer transition-colors ${
                    record.status === 'absent'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                      : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}>
                    <input
                      type="checkbox"
                      checked={record.status === 'absent'}
                      onChange={() => toggleAttendance(record.userId, 'absent')}
                      className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                    />
                    Absent
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Task Tracker Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <h3 className="font-medium text-gray-900 dark:text-gray-100">Task Tracker</h3>
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {tasksCompletedCount}/{tasks.length} completed
          </span>
        </div>

        {tasks.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 font-medium w-10"></th>
                  <th className="px-3 py-2 font-medium">Auditor</th>
                  <th className="px-3 py-2 font-medium">Task</th>
                  <th className="px-3 py-2 font-medium w-32">Due Date</th>
                  <th className="px-3 py-2 font-medium w-32">Status</th>
                  <th className="px-3 py-2 font-medium w-16">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {tasks.map(task => (
                  <tr key={task.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    {/* Checkbox */}
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={task.isComplete}
                        onChange={() => toggleTaskComplete(task.id)}
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                      />
                    </td>

                    {/* Auditor Dropdown */}
                    <td className="px-3 py-2">
                      <select
                        value={task.auditorId}
                        onChange={(e) => {
                          const selectedUser = cyberSweepUsers.find(u => u.id === e.target.value);
                          updateTask(task.id, {
                            auditorId: e.target.value,
                            auditorName: selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : '',
                          });
                        }}
                        className="w-full px-2 py-1 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">Select auditor...</option>
                        {cyberSweepUsers.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.firstName} {user.lastName}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Task Text */}
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={task.task}
                        onChange={(e) => updateTask(task.id, { task: e.target.value })}
                        placeholder="Enter task description..."
                        className={`w-full px-2 py-1 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                          task.isComplete ? 'line-through text-gray-400 dark:text-gray-500' : ''
                        }`}
                      />
                    </td>

                    {/* Due Date */}
                    <td className="px-3 py-2">
                      <input
                        type="date"
                        value={task.dueDate}
                        onChange={(e) => updateTask(task.id, { dueDate: e.target.value })}
                        className="px-2 py-1 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </td>

                    {/* Status */}
                    <td className="px-3 py-2">
                      <select
                        value={task.status}
                        onChange={(e) => updateTask(task.id, { status: e.target.value as TaskItem['status'] })}
                        className={`px-2 py-1 text-xs font-medium rounded cursor-pointer ${getTaskStatusStyles(task.status)}`}
                      >
                        {TASK_STATUS_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </td>

                    {/* Delete */}
                    <td className="px-3 py-2">
                      <button
                        onClick={() => deleteTask(task.id)}
                        className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded transition-colors"
                        title="Delete task"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No tasks added yet
          </div>
        )}

        {/* Add Task Button */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
          <button
            onClick={addTask}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Task
          </button>
        </div>
      </div>

      {/* Agenda Checklist */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <h3 className="font-medium text-gray-900 dark:text-gray-100">Agenda Checklist</h3>
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {completedCount}/{agendaItems.length} completed
          </span>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {agendaItems.map(item => (
            <div
              key={item.id}
              className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 group"
            >
              {/* First row: Checkbox + Text + Delete */}
              <div className="flex items-center gap-3">
                {/* Drag handle (visual only for now) */}
                <GripVertical className="h-4 w-4 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab flex-shrink-0" />

                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={item.completed}
                  onChange={() => toggleAgendaItem(item.id)}
                  className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer flex-shrink-0"
                />

                {/* Text (editable) */}
                <input
                  type="text"
                  value={item.text}
                  onChange={(e) => updateAgendaItemText(item.id, e.target.value)}
                  className={`flex-1 bg-transparent border-none outline-none text-sm font-medium focus:ring-0 ${
                    item.completed
                      ? 'text-gray-400 dark:text-gray-500 line-through'
                      : 'text-gray-900 dark:text-gray-100'
                  }`}
                  placeholder="Agenda item..."
                />

                {/* Delete button (only for custom items) */}
                {!item.isDefault && (
                  <button
                    onClick={() => deleteAgendaItem(item.id)}
                    className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    title="Delete item"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Second row: Notes - show textarea or "+ Add notes" button */}
              <div className="ml-11 mt-2">
                {item.showNotes || item.notes ? (
                  <textarea
                    value={item.notes}
                    onChange={(e) => updateAgendaItemNotes(item.id, e.target.value)}
                    onBlur={(e) => handleNotesBlur(item.id, e.target.value)}
                    placeholder="Add notes or discussion points..."
                    rows={2}
                    autoFocus={item.showNotes && !item.notes}
                    className={`w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 placeholder-gray-400 resize-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 ${
                      item.completed ? 'opacity-50' : ''
                    }`}
                  />
                ) : (
                  <button
                    onClick={() => toggleNotesVisibility(item.id, true)}
                    className="flex items-center gap-1 text-sm text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 transition-colors"
                  >
                    <MessageSquarePlus className="h-3.5 w-3.5" />
                    Add notes
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Add new item */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newItemText}
              onChange={(e) => setNewItemText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addAgendaItem()}
              placeholder="Add new agenda item..."
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400"
            />
            <button
              onClick={addAgendaItem}
              disabled={!newItemText.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="h-4 w-4" />
              Add Item
            </button>
          </div>
        </div>
      </div>

      {/* Sync Status Footer */}
      {agendaRecord && (
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 px-4 py-2">
          <div className="flex items-center gap-2 text-xs text-green-700 dark:text-green-300">
            <Cloud className="h-3.5 w-3.5" />
            <span>Data synced with Gobodo • Auto-saves on changes</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingAgendaTab;
